import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Button } from '../ui/Button';

interface FAQItem {
  question: string;
  answer: string;
  category: string;
}

export const FaqPage: React.FC = () => {
  const { t } = useTranslation();
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());

  const categories = [
    { id: 'all', name: 'All Questions', icon: 'fas fa-question-circle' },
    { id: 'general', name: 'General', icon: 'fas fa-info-circle' },
    { id: 'technical', name: 'Technical', icon: 'fas fa-cog' },
    { id: 'investment', name: 'Investment', icon: 'fas fa-chart-line' },
    { id: 'security', name: 'Security', icon: 'fas fa-shield-alt' }
  ];

  const faqItemsRaw = t('pages.faq.items', { returnObjects: true });
  const faqItems: FAQItem[] = Array.isArray(faqItemsRaw) ? faqItemsRaw as FAQItem[] : [];

  const toggleExpanded = (index: number) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedItems(newExpanded);
  };

  const filteredItems = activeCategory === 'all'
    ? faqItems
    : faqItems.filter(item => {
        if (!item.category) return false;
        return item.category === activeCategory;
      });

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="floating-element"></div>
        <div className="floating-element"></div>
        <div className="floating-element"></div>
      </div>

      {/* Hero Section */}
      <section className="relative min-h-[50vh] flex items-center justify-center overflow-hidden">
        <div className="hero-background absolute inset-0">
          <div
            className="absolute inset-0 opacity-30"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3Cpattern id='grid' width='100' height='100' patternUnits='userSpaceOnUse'%3E%3Cpath d='M 100 0 L 0 0 0 100' fill='none' stroke='rgba(212, 175, 55, 0.1)' stroke-width='1'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width='100%25' height='100%25' fill='url(%23grid)' /%3E%3C/svg%3E")`,
            }}
          />
        </div>

        <div className="relative z-10 text-center px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto">
          <div className="inline-block px-4 py-2 bg-gold/10 rounded-full mb-6">
            <span className="text-gold font-semibold text-sm uppercase tracking-wider">{t('pages.faq.eyebrow')}</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-black text-white mb-8 leading-tight">
            {t('pages.faq.title')}
          </h1>
          <p className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
            {t('pages.faq.intro')}
          </p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="section relative">
        <div className="container">
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`group relative px-6 py-3 rounded-full transition-all duration-300 ${
                  activeCategory === category.id
                    ? 'bg-gold text-black font-bold'
                    : 'bg-gold/10 text-gold hover:bg-gold/20'
                }`}
              >
                <span className="relative z-10 flex items-center gap-2">
                  <i className={`${category.icon} text-sm`}></i>
                  {category.name}
                </span>
                {activeCategory === category.id && (
                  <div className="absolute inset-0 bg-gradient-to-r from-gold to-gold-light rounded-full"></div>
                )}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Items */}
      <section className="section relative">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <div className="space-y-6">
              {filteredItems.map((item, index) => (
                <div
                  key={index}
                  className={`modern-card group cursor-pointer transition-all duration-300 ${
                    expandedItems.has(index) ? 'ring-2 ring-gold/50' : ''
                  }`}
                  onClick={() => toggleExpanded(index)}
                >
                  <div className="flex items-center justify-between">
                    <h3 className="text-xl font-bold text-white pr-4 group-hover:text-gold transition-colors duration-300">
                      {item.question}
                    </h3>
                    <div className={`flex-shrink-0 w-12 h-12 bg-gradient-to-br from-gold/20 to-gold/5 rounded-full flex items-center justify-center transition-all duration-300 ${
                      expandedItems.has(index) ? 'rotate-180' : ''
                    }`}>
                      <i className="fas fa-chevron-down text-gold"></i>
                    </div>
                  </div>

                  <div className={`overflow-hidden transition-all duration-300 ${
                    expandedItems.has(index) ? 'max-h-96 mt-6' : 'max-h-0'
                  }`}>
                    <div className="pt-4 border-t border-gold/20">
                      <p className="text-gray-400 leading-relaxed">
                        {item.answer}
                      </p>
                    </div>
                  </div>

                  <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-gold to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Quick Help Section */}
      <section className="section bg-bg-secondary/50 relative">
        <div className="container">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-gold/10 rounded-full mb-6">
              <span className="text-gold font-semibold text-sm uppercase tracking-wider">Quick Help</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-white mb-6">Need More Assistance?</h2>
            <p className="text-xl text-gray-400 max-w-4xl mx-auto leading-relaxed">
              Our support team is here to help you with any questions
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="glass-card text-center group">
              <div className="w-20 h-20 bg-gradient-to-br from-gold/20 to-gold/5 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                <i className="fas fa-envelope text-4xl text-gold"></i>
              </div>
              <h3 className="text-xl font-bold text-white mb-4 group-hover:text-gold transition-colors">Email Support</h3>
              <p className="text-gray-400 mb-4">
                Get detailed help via email
              </p>
              <a
                href={`mailto:${t('pages.faq.cta.email')}`}
                className="inline-flex items-center gap-2 text-gold hover:text-gold-light font-semibold transition-all"
              >
                {t('pages.faq.cta.email')}
                <i className="fas fa-arrow-right"></i>
              </a>
            </div>

            <div className="glass-card text-center group">
              <div className="w-20 h-20 bg-gradient-to-br from-gold/20 to-gold/5 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                <i className="fas fa-comments text-4xl text-gold"></i>
              </div>
              <h3 className="text-xl font-bold text-white mb-4 group-hover:text-gold transition-colors">Live Chat</h3>
              <p className="text-gray-400 mb-4">
                Chat with our support team
              </p>
              <Link to="/resources" className="inline-flex items-center gap-2 text-gold hover:text-gold-light font-semibold transition-all">
                Start Chat
                <i className="fas fa-arrow-right"></i>
              </Link>
            </div>

            <div className="glass-card text-center group">
              <div className="w-20 h-20 bg-gradient-to-br from-gold/20 to-gold/5 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                <i className="fas fa-book text-4xl text-gold"></i>
              </div>
              <h3 className="text-xl font-bold text-white mb-4 group-hover:text-gold transition-colors">Documentation</h3>
              <p className="text-gray-400 mb-4">
                Browse our comprehensive guides
              </p>
              <Link to="/resources" className="inline-flex items-center gap-2 text-gold hover:text-gold-light font-semibold transition-all">
                View Docs
                <i className="fas fa-arrow-right"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section pb-20">
        <div className="container">
          <div className="cta-section relative">
            <h2 className="text-4xl md:text-5xl font-black text-white mb-6 relative z-10">{t('pages.faq.cta.title')}</h2>
            <p className="text-xl text-gray-400 mb-12 max-w-3xl mx-auto leading-relaxed relative z-10">
              {t('pages.faq.cta.description')}
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center relative z-10">
              <a
                href={`mailto:${t('pages.faq.cta.email')}`}
                className="group relative overflow-hidden px-8 py-4 text-lg font-bold bg-gradient-to-r from-gold to-gold-light text-black rounded-full hover:from-indonesia-red hover:to-gold transition-all duration-300"
              >
                <span className="relative z-10 flex items-center">
                  <i className="fas fa-envelope mr-3"></i>
                  {t('pages.faq.cta.button')}
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-indonesia-red to-gold opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-full"></div>
              </a>
              <Link to="/resources">
                <Button variant="secondary" size="lg" className="group relative overflow-hidden px-8 py-4 text-lg font-bold">
                  <span className="relative z-10">Browse Resources</span>
                  <div className="absolute inset-0 bg-gradient-to-r from-gold/20 to-gold/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};